import { Dimensions } from 'react-native';
import { initialWindowMetrics } from 'react-native-safe-area-context';
import {
  FIXED_TOUR_ANCHORS,
  TOUR_TOOLTIP_ESTIMATED_HEIGHT,
  TOUR_TOOLTIP_GAP,
  TOUR_VIEWPORT_PADDING,
} from '../constants';
import type { TTourAnchorRect } from '../types';

export type TTourScrollHandle = {
  scrollBy: (dy: number) => void;
  measureViewport: () => Promise<TTourAnchorRect | null>;
};

export type TTourBounds = {
  top: number;
  bottom: number;
  height: number;
};

const MIN_SPOT_HEIGHT = 24;

const MAX_TOOLTIP_SHARE = 0.6;

let handle: TTourScrollHandle | null = null;
let viewport: TTourAnchorRect | null = null;

export const registerTourScrollView = (next: TTourScrollHandle): (() => void) => {
  handle = next;

  return () => {
    if (handle === next) {
      handle = null;
      viewport = null;
    }
  };
};

export const getTourScrollHandle = (): TTourScrollHandle | null => handle;

export const refreshTourViewport = async (): Promise<void> => {
  viewport = (await handle?.measureViewport()) ?? null;
};

const toBounds = (top: number, bottom: number): TTourBounds => ({
  top,
  bottom: Math.max(top, bottom),
  height: Math.max(0, bottom - top),
});

const windowBounds = (): TTourBounds => {
  const { height } = Dimensions.get('window');
  const insets = initialWindowMetrics?.insets;

  return toBounds(
    (insets?.top ?? 0) + TOUR_VIEWPORT_PADDING,
    height - (insets?.bottom ?? 0) - TOUR_VIEWPORT_PADDING,
  );
};

export const tourSpotBounds = (name: string): TTourBounds => {
  const window = windowBounds();

  if (FIXED_TOUR_ANCHORS.includes(name) || !viewport) {
    return window;
  }

  return toBounds(
    Math.max(window.top, viewport.y + TOUR_VIEWPORT_PADDING),
    Math.min(window.bottom, viewport.y + viewport.height - TOUR_VIEWPORT_PADDING),
  );
};

let tooltipHeight = TOUR_TOOLTIP_ESTIMATED_HEIGHT;

const tooltipListeners = new Set<() => void>();

export const subscribeTourTooltipHeight = (listener: () => void): (() => void) => {
  tooltipListeners.add(listener);

  return () => {
    tooltipListeners.delete(listener);
  };
};

export const setTourTooltipHeight = (height: number): void => {
  const next = Math.round(height);

  if (next <= 0 || next === tooltipHeight) {
    return;
  }

  tooltipHeight = next;
  tooltipListeners.forEach(listener => listener());
};

export const tourTooltipReserve = (bounds: TTourBounds): number =>
  Math.min(tooltipHeight + TOUR_TOOLTIP_GAP, bounds.height * MAX_TOOLTIP_SHARE);

export const maxTourSpotHeight = (bounds: TTourBounds): number =>
  Math.max(MIN_SPOT_HEIGHT, bounds.height - tourTooltipReserve(bounds));

const snapDown = (edges: number[], edge: number): number =>
  edges.filter(value => value <= edge).reduce((closest, value) => Math.max(closest, value), -Infinity);

const snapUp = (edges: number[], edge: number): number =>
  edges.filter(value => value >= edge).reduce((closest, value) => Math.min(closest, value), Infinity);

export const clampToTourBounds = (
  rect: TTourAnchorRect,
  bounds: TTourBounds,
  items: TTourAnchorRect[] = [],
): TTourAnchorRect => {
  const anchorBottom = rect.y + rect.height;
  const limit = maxTourSpotHeight(bounds);

  let top = Math.max(rect.y, bounds.top);

  if (top > rect.y) {
    const snapped = snapUp(items.map(item => item.y), top);

    if (snapped < anchorBottom) {
      top = snapped;
    }
  }

  let bottom = Math.min(anchorBottom, bounds.bottom, top + limit);

  if (bottom < anchorBottom) {
    const snapped = snapDown(items.map(item => item.y + item.height), bottom);

    if (snapped > top) {
      bottom = snapped;
    }
  }

  if (bottom - top >= MIN_SPOT_HEIGHT) {
    return { ...rect, y: top, height: bottom - top };
  }

  const height = Math.min(rect.height, limit);

  return {
    ...rect,
    y: rect.y < bounds.top ? bounds.top : bounds.bottom - height,
    height,
  };
};
